/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { ChangeEvent, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Card,
  CardActions,
  CardContent,
} from "@material-ui/core";
import { Edit } from "@material-ui/icons";
import { fetchHeaders } from "../../../store/actions/api";
import { ApiRequestHeader } from "../../../data/interfaces";
import { useStyles } from "./style";

interface SecurityHeaderProps {
  headerName: string;
  headerValue: string;
  setOpenSecurityHeader: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function SecurityHeader({
  headerName,
  headerValue,
  setOpenSecurityHeader,
}: SecurityHeaderProps) {
  const classes = useStyles();
  const intl = useIntl();
  const [requestHeader, setRequestHeader] = useState({ 
    headerName: "Authorization", 
    headerValue: "" 
  });
  const [isAdded, setIsAdded] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  const dispatch = useDispatch();

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === "headerName") {
      setRequestHeader((prevHeader) => ({
        ...prevHeader,
        headerName: value,
      }));
    } else {
      setRequestHeader((prevHeader) => ({
        ...prevHeader,
        headerValue: value,
      }));
    }
  };

  const handleSave = () => {
    const headerValues: ApiRequestHeader = {
      headerName: requestHeader.headerName,
      headerValue: requestHeader.headerValue,
    };
    dispatch(fetchHeaders(headerValues));
  };

  const resetValues = () => {
    setRequestHeader({ headerName: "", headerValue: "" });
    const headerValues: ApiRequestHeader = {
      headerName: "",
      headerValue: "",
    };
    dispatch(fetchHeaders(headerValues));
  };

  useEffect(() => {
    if (headerName === undefined && headerValue === undefined) {
      setIsAdded(false);
    } else if (headerName === '' && headerValue === '') {
      setIsAdded(false);
    } else {
      setIsAdded(true);
      setIsClicked(true);
    }
  }, [headerName, headerValue]);

  useEffect(() => {
    if (
      requestHeader.headerName === '' ||
      requestHeader.headerValue === ''
    ) {
      setIsEmpty(true);
    } else {
      setIsEmpty(false);
    }
  }, [requestHeader]);

  useEffect(() => {
    if (headerName === '') {
      setRequestHeader((prevHeader) => ({
        ...prevHeader,
        headerName: "Authorization",
      }));
    } else {
    setRequestHeader({ headerName: headerName, headerValue: headerValue });
    }
  }, [headerName, headerValue]);

  return (
    <Box display="block" justifyContent="left" alignItems="flex-start">
      {!isAdded ? (
        <Box className={classes.securityHeaderContainer} my={2} >
          <Box className={classes.securityHeaderLeftBox} />
          <Box className={classes.securityHeaderRightBox}>
            <Box className={classes.securityHeaderCard}>
              <Card variant="outlined">
                <CardContent>
                  <Box textAlign="start">
                    <Typography component="h2" gutterBottom variant="h5">
                      Add Security Header
                    </Typography>
                  </Box>
                  <Box my={2}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item xs={4}>
                        <TextField
                          name="headerName"
                          placeholder={intl.formatMessage({
                            id: "api.apiTryOut.messages.headerName.input.placeHolder",
                            defaultMessage: "Name",
                          })}
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={requestHeader.headerName}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={8}>
                        <TextField
                          name="headerValue"
                          placeholder={intl.formatMessage({
                            id: "api.apiTryOut.messages.headerValue.input.placeHolder",
                            defaultMessage: "Value",
                          })}
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={requestHeader.headerValue}
                          onChange={handleInputChange}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
                <CardActions>
                  <Box className={classes.cardActionBox}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setOpenSecurityHeader(false);
                        setIsClicked(false);
                        resetValues();
                      }}
                      className={classes.securityHeaderButton}>
                        {!isClicked ? (
                          <FormattedMessage
                            id="api.apiTryOut.messages.securityHeader.cancel.label"
                            defaultMessage='Cancel'
                          />
                        ) : (
                        <FormattedMessage
                          id="api.apiTryOut.messages.securityHeader.remove.label"
                          defaultMessage='Remove'
                        />
                        )}
                    </Button>
                    <Box className={classes.cardActionSpace} />
                    <Button
                      disabled={isEmpty}
                      color="primary"
                      variant="contained"
                      size="small"
                      onClick={handleSave}
                      className={classes.securityHeaderButton}>
                        {!isClicked ? (
                          <FormattedMessage
                          id="api.apiTryOut.messages.securityHeader.add.label"
                          defaultMessage='Add'
                        />
                        ) : (
                          <FormattedMessage
                          id="api.apiTryOut.messages.securityHeader.update.label"
                          defaultMessage='Update'
                        />
                        )}
                    </Button>
                  </Box>
                </CardActions>
              </Card>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box className={classes.securityHeaderContainer} my={2}>
          <Box className={classes.securityHeaderLeftBox}>
            <FormattedMessage
              id="api.apiTryOut.messages.securityHeader.label"
              defaultMessage="Security Header"
            />
          </Box>
          <Box className={classes.securityHeaderRightBox}>
            <Box className={classes.securityHeaderDetailBox}>
              <Box flex={2}>
                <TextField
                  name="headerName"
                  className={classes.securityHeaderInput}
                  placeholder={intl.formatMessage({
                    id: "api.apiTryOut.messages.headerName.input.placeHolder",
                    defaultMessage: "Name",
                  })}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  size="small"
                  value={requestHeader.headerName}
                  onChange={handleInputChange}
                />
              </Box>
              <Box className={classes.cardActionSpace} />
              <Box flex={4}>
                <TextField
                  name="headerValue"
                  className={classes.securityHeaderInput}
                  placeholder={intl.formatMessage({
                    id: "api.apiTryOut.messages.headerValue.input.placeHolder",
                    defaultMessage: "Value",
                  })}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  size="small"
                  value={requestHeader.headerValue}
                  onChange={handleInputChange}
                />
              </Box>
              <Box className={classes.cardActionSpace} />
              <Box>
                <Button
                  color="primary"
                  startIcon={<Edit />}
                  onClick={() => {
                    setIsAdded(false);
                  }}
                  className={classes.securityHeaderButton}>
                  <FormattedMessage
                    id="api.apiTryOut.messages.secuirtyHeader.edit.label"
                    defaultMessage="Edit"
                  />
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
